import React, { useEffect, useState } from 'react';

import { List, Datagrid, TextField, DeleteButton, Edit, SelectInput, TextInput, Button, Create, SimpleForm, TopToolbar, FormTab, TabbedForm, FormDataConsumer, required, NumberInput, BooleanInput, ResourceContextProvider, useCreate, useRefresh, FormWithRedirect, Toolbar, SaveButton, useNotify, useRedirect, BooleanField, ReferenceInput, ReferenceField, useDataProvider, DateTimeInput } from 'react-admin';import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { makeStyles } from '@material-ui/core/styles';

import { useTranslate  } from '../common/language';

import Typography from '@mui/material/Typography';


import { TextFieldShow } from '../components/TextFieldShow';

import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import IconContentAdd from '@material-ui/icons/Add';
import IconCancel from '@material-ui/icons/Cancel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
  

import { RenderBoolean } from '../components/User';

import { Link } from 'react-router-dom';

import { validateMaxLength } from './../components/Validator';
import { dateAndTimeToCatalan, dateParser } from '../common/utils';

const finishAfterStart = (startSource, enabledSource) => (finish, values) => {
  if (!values || (enabledSource && !values[enabledSource]) || !finish || !values[startSource]) return undefined;
  const startTime = new Date(values[startSource]).getTime();
  const finishTime = new Date(finish).getTime();
  if (Number.isNaN(startTime) || Number.isNaN(finishTime)) return undefined;
  return finishTime > startTime ? undefined : 'common.validation.date_finish_after_start';
};

const AUDIENCE_TYPE_EVERYONE = 0;
const AUDIENCE_TYPE_CODE = 2;
const AUDIENCE_TYPE_CONTACT_GROUP = 3;

const audienceTypeLabel = (translate, type) => translate('events_sessions_prices.audience_type_' + (type || AUDIENCE_TYPE_EVERYONE));
const sessionHasMap = session => session && session.maps_id != null;

const useEventSession = eventSessionId => {
  const dataProvider = useDataProvider();
  const [eventSession, setEventSession] = useState(null);

  useEffect(() => {
    if (!eventSessionId) {
      setEventSession(null);
      return;
    }
    dataProvider.getOne('events_sessions', { id: eventSessionId })
      .then(({ data }) => setEventSession(data))
      .catch(() => setEventSession(null));
  }, [dataProvider, eventSessionId]);

  return eventSession;
};

const useTicketZones = ticketsMapId => {
  const dataProvider = useDataProvider();
  const [ticketZones, setTicketZones] = useState({});

  useEffect(() => {
    if (!ticketsMapId) {
      setTicketZones({});
      return;
    }
    dataProvider.getList('tickets_zones', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' },
      filter: { tickets_maps_id: ticketsMapId },
    })
      .then(({ data }) => setTicketZones(data.reduce((zones, zone) => ({ ...zones, [zone.id]: zone }), {})))
      .catch(() => setTicketZones({}));
  }, [dataProvider, ticketsMapId]);

  return ticketZones;
};

const TicketZoneNameField = ({ record, ticketZones }) => (
  <span>{record && ticketZones[record.tickets_zones_id] ? ticketZones[record.tickets_zones_id].name : ''}</span>
);

const TicketZoneCapacityField = ({ record, ticketZones }) => (
  <span>{record && ticketZones[record.tickets_zones_id] ? ticketZones[record.tickets_zones_id].sale_capacity : ''}</span>
);

const PriceZoneInput = ({ eventSessionId }) => {
  const translate = useTranslate();
  const eventSession = useEventSession(eventSessionId);

  if (!sessionHasMap(eventSession)) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <ReferenceInput
        label={translate('tickets_zones.name')}
        source="tickets_zones_id"
        reference="tickets_zones"
        filter={{ tickets_maps_id: eventSession.maps_id }}
        perPage={1000}
        sort={{ field: 'name', order: 'ASC' }}
        fullWidth
        validate={[required()]}
      >
        <SelectInput optionText="name" optionValue="id" resettable />
      </ReferenceInput>
    </Grid>
  );
};

const PriceTotalCapacityInput = ({ eventSessionId }) => {
  const translate = useTranslate();
  const eventSession = useEventSession(eventSessionId);

  if (sessionHasMap(eventSession)) {
    return null;
  }

  return (
    <Grid item xs={3}>
      <TextInput fullWidth source="total_capacity" label={translate('events_sessions_prices.total_capacity')} validate={[required()]} />
    </Grid>
  );
};

const PriceAudienceConfigurationFields = ({ formData }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [groupAudienceType, setGroupAudienceType] = useState(null);

  useEffect(() => {
    if (!formData || !formData.tickets_groups_id) {
      setGroupAudienceType(null);
      return;
    }
    dataProvider.getOne('tickets_groups', { id: formData.tickets_groups_id })
      .then(({ data }) => setGroupAudienceType(data && data.audience_type !== undefined && data.audience_type !== null ? Number(data.audience_type) : AUDIENCE_TYPE_EVERYONE))
      .catch(() => setGroupAudienceType(null));
  }, [dataProvider, formData && formData.tickets_groups_id]);

  if (groupAudienceType === AUDIENCE_TYPE_CONTACT_GROUP) {
    return (
      <Grid item xs={12}>
        <NumberInput fullWidth source="audience_max_tickets_per_contact_session" label={translate('events_sessions_prices.audience_max_tickets_per_contact_session')} />
      </Grid>
    );
  }

  return null;
};

const PriceAudienceDetails = ({ record }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [ticketGroup, setTicketGroup] = useState(null);
  const [audienceGroup, setAudienceGroup] = useState(null);

  useEffect(() => {
    if (!record || !record.tickets_groups_id) {
      setTicketGroup(null);
      return;
    }
    dataProvider.getOne('tickets_groups', { id: record.tickets_groups_id })
      .then(({ data }) => setTicketGroup(data))
      .catch(() => setTicketGroup(null));
  }, [dataProvider, record && record.tickets_groups_id]);

  useEffect(() => {
    if (!ticketGroup || Number(ticketGroup.audience_type) !== AUDIENCE_TYPE_CONTACT_GROUP || !ticketGroup.audience_groups_id) {
      setAudienceGroup(null);
      return;
    }
    dataProvider.getOne('groups', { id: ticketGroup.audience_groups_id })
      .then(({ data }) => setAudienceGroup(data))
      .catch(() => setAudienceGroup(null));
  }, [dataProvider, ticketGroup]);

  if (!ticketGroup) {
    return null;
  }

  const audienceType = ticketGroup.audience_type !== undefined && ticketGroup.audience_type !== null ? Number(ticketGroup.audience_type) : AUDIENCE_TYPE_EVERYONE;

  return (
    <>
      <Grid item xs={4}>   
        <TextFieldShow source={audienceTypeLabel(translate, audienceType)} label={translate('events_sessions_prices.audience_type')} />
      </Grid>
      {audienceType === AUDIENCE_TYPE_CODE ? (
        <>
          <Grid item xs={4}>   
            <TextFieldShow source={ticketGroup.audience_code_min_length} label={translate('events_sessions_prices.audience_code_min_length')} />
          </Grid>
          <Grid item xs={4}>   
            <TextFieldShow source={ticketGroup.audience_code_max_length} label={translate('events_sessions_prices.audience_code_max_length')} />
          </Grid>
        </>
      ) : null}
      {audienceType === AUDIENCE_TYPE_CONTACT_GROUP ? (
        <>
          <Grid item xs={4}>   
            <TextFieldShow source={audienceGroup ? audienceGroup.name : ''} label={translate('events_sessions_prices.audience_groups_id')} />
          </Grid>
          <Grid item xs={4}>   
            <TextFieldShow source={record.audience_max_tickets_per_contact_session} label={translate('events_sessions_prices.audience_max_tickets_per_contact_session')} />
          </Grid>
        </>
      ) : null}
    </>
  );
};

const PriceAudienceField = ({ record }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [label, setLabel] = useState('');

  useEffect(() => {
    let mounted = true;
    if (!record || !record.tickets_groups_id) {
      setLabel('');
      return undefined;
    }

    dataProvider.getOne('tickets_groups', { id: record.tickets_groups_id })
      .then(async ({ data: ticketGroup }) => {
        const audienceType = ticketGroup && ticketGroup.audience_type != null
          ? Number(ticketGroup.audience_type)
          : AUDIENCE_TYPE_EVERYONE;
        let audienceLabel = audienceTypeLabel(translate, audienceType);

        if (audienceType === AUDIENCE_TYPE_CONTACT_GROUP && ticketGroup.audience_groups_id) {
          try {
            const { data: group } = await dataProvider.getOne('groups', { id: ticketGroup.audience_groups_id });
            if (group && group.name) audienceLabel += ': ' + group.name;
          } catch (error) {
            // Manté el tipus de públic encara que el grup ja no sigui accessible.
          }
        }

        if (mounted) setLabel(audienceLabel);
      })
      .catch(() => {
        if (mounted) setLabel('');
      });

    return () => {
      mounted = false;
    };
  }, [dataProvider, record && record.tickets_groups_id, translate]);

  return <span>{label}</span>;
};

const PriceDeleteButton = props => {
  const translate = useTranslate();
  const stopPropagation = event => event.stopPropagation();

  return (
    <span onClick={stopPropagation} onMouseDown={stopPropagation} onPointerDown={stopPropagation}>
      <DeleteButton
        {...props}
        label=""
        undoable={false}
        redirect={false}
        confirmTitle={translate('events_sessions_prices.delete_title')}
        confirmContent={translate('events_sessions_prices.delete_ask')}
      />
    </span>
  );
};

export const EventsSessionsPricesList = (props) => {
  const translate = useTranslate();  
  const eventSession = useEventSession(props.events_sessions_id);
  const hasMap = sessionHasMap(eventSession);
  const ticketZones = useTicketZones(hasMap ? eventSession.maps_id : null);
    
  let basePath='/events_sessions_prices'; 
  let resource = 'events_sessions_prices';
    
  return (
    <ResourceContextProvider value={resource}>
      <List 
        basePath={basePath}   
        filter={{ events_sessions_id : props.events_sessions_id }}  
        actions={props.show ?  <span/> : <ListActions events_sessions_id={props.events_sessions_id} />}    
        empty={props.show ?  <span/> : <ListActions events_sessions_id={props.events_sessions_id} />}        
        bulkActionButtons={false}          
        perPage={1000}   
        pagination={false}
        sort={{ field: 'id', order: 'ASC' }} 
        label=""  
        title=""  
        resource={resource}
      >        
        <Datagrid 
          rowClick={props.show ? null : 'edit'}
          expand={<EventsSessionsPricesDetails hasMap={hasMap} />} > 
          <Avatar><ShoppingCartIcon/></Avatar>                                  
          {hasMap ? <TicketZoneNameField label={translate('tickets_zones.name')} ticketZones={ticketZones} /> : null}
          <ReferenceField label={translate('events_sessions_prices.tickets_groups_id')} source="tickets_groups_id" reference="tickets_groups" link={false} > 
            <TextField source="name" />                               
          </ReferenceField>
          <TextField source="price" label={translate('events_sessions_prices.price')} sortable={false} />            
          <PriceAudienceField label={translate('events_sessions_prices.audience_type')} />
          {hasMap
            ? <TicketZoneCapacityField label={translate('events_sessions_prices.total_capacity')} ticketZones={ticketZones} />
            : <TextField source="total_capacity" label={translate('events_sessions_prices.total_capacity')} sortable={false} />}
          <BooleanField source="actived" label={translate('events_sessions_prices.actived')} sortable={false} />
          <BooleanField source="box_office_sale_enabled" label={translate('events_sessions_prices.box_office_sale_enabled')} sortable={false} />
          <BooleanField source="box_office_reservation_enabled" label={translate('events_sessions_prices.box_office_reservation_enabled')} sortable={false} />
          {!props.show ? <PriceDeleteButton /> : null}
        </Datagrid>        
      </List>
    </ResourceContextProvider>
  );
};


export const EventsSessionsPricesDetails = ({ id, record, resource, hasMap, ...props }) => {
  const translate = useTranslate(); 
        
  return (
    <div className="usersClassroomsExpand">        
      <Grid container spacing={2}>
        <Grid item xs={4}>             
          <TextFieldShow source={<ReferenceField label={translate('events_sessions_prices.tickets_groups_id')} source="tickets_groups_id" reference="tickets_groups" link={false} > 
            <TextField source="name" />                               
          </ReferenceField>  
          } label={translate('events_sessions_prices.tickets_groups_id')} />
        </Grid> 
        {!hasMap ? <Grid item xs={4}>   
          <TextFieldShow source={record.total_capacity} label={translate('events_sessions_prices.total_capacity')} />
        </Grid> : null}
        <Grid item xs={4}>   
          <TextFieldShow source={record.price} label={translate('events_sessions_prices.price')} />
        </Grid> 
        <PriceAudienceDetails record={record} />

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.online_sales')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={<RenderBoolean source={record.actived} />} label={translate('events_sessions_prices.actived')} />
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={dateAndTimeToCatalan(record.sale_start)} label={translate('events_sessions_prices.sale_start')} />
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={dateAndTimeToCatalan(record.sale_finish)} label={translate('events_sessions_prices.sale_finish')} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.box_office_sales')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={<RenderBoolean source={record.box_office_sale_enabled} />} label={translate('events_sessions_prices.box_office_sale_enabled')} />
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={dateAndTimeToCatalan(record.box_office_start)} label={translate('events_sessions_prices.box_office_start')} />
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={dateAndTimeToCatalan(record.box_office_finish)} label={translate('events_sessions_prices.box_office_finish')} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.reservations')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={<RenderBoolean source={record.box_office_reservation_enabled} />} label={translate('events_sessions_prices.box_office_reservation_enabled')} />
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={dateAndTimeToCatalan(record.reservation_period_start)} label={translate('events_sessions_prices.reservation_period_start')} />
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={dateAndTimeToCatalan(record.reservation_period_finish)} label={translate('events_sessions_prices.reservation_period_finish')} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.validators')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <TextFieldShow source={<RenderBoolean source={record.require_check} />} label={translate('tickets_groups.require_check')} />
        </Grid>
        <Grid item xs={8}>
          <TextFieldShow source={record.require_check_message} label={translate('tickets_groups.require_check_message')} />
        </Grid>
      </Grid>
      
    </div>
  );
};

const ListActions = (props) => {
    const translate = useTranslate();     
    return (
    <TopToolbar>     
        <MyCreateButton {...props} />   
    </TopToolbar>
    );
};

export const MyCreateButton = ({ ...props }) => {

  const [showDialog, setShowDialog] = useState(false);
  const [create, { loading }] = useCreate('events_sessions_prices');
  const notify = useNotify();
  const refresh = useRefresh();    
  const translate = useTranslate();  
  const redirect = useRedirect();
        
  const handleClick = () => {
    setShowDialog(true);
  };

  const handleCloseClick = () => {
    setShowDialog(false);
  };
    
const handleSubmit = async values => {
    create(            
      { payload: { data: values } },
      {
        onSuccess: ({ data }) => {                   

          redirect('/events_sessions/' + props.events_sessions_id + '/prices');
          setShowDialog(false);
          refresh();

        },
        onFailure: ( error ) => {
          notify( error.message , 'error');
        }
      }
    );
  };
    
  return (
    <>            
      <Button onClick={handleClick} label={translate('events_sessions_prices.create')}>
        <IconContentAdd />                
      </Button>

      <Dialog
        fullWidth
        open={showDialog}
        onClose={handleCloseClick}
        aria-label={translate('events_sessions_prices.create')}
      >
        <DialogTitle>{translate('events_sessions_prices.create')}</DialogTitle>

        <FormWithRedirect
          save={handleSubmit}
          initialValues={{
            events_sessions_id: props.events_sessions_id,
            audience_max_tickets_per_contact_session: 1,
          }}
          render={({
            handleSubmitWithRedirect,
            pristine,
            saving
          }) => (
            <>
              <DialogContent>
                <FormDataConsumer>
                  {({ formData }) => (
                    <Grid container spacing={1}>                                     
                      <PriceZoneInput eventSessionId={props.events_sessions_id} />
                      <Grid item xs={9}>   
                      
                        <ReferenceInput                          
                            label={translate('events_sessions_prices.tickets_groups_id')}
                            source="tickets_groups_id" 
                            reference="tickets_groups" 
                            filter={{ associations_id: localStorage.getItem('associations_id') }} 
                            perPage={1000}
                            sort={{ field: 'priority', order: 'ASC' }}     
                            fullWidth
                            validate={[required()]}
                        >
                           <SelectInput optionText="name" optionValue="id" resettable />
                        </ReferenceInput>
        
                      </Grid>  
                      
                      <Grid item xs={3}>   
                        <NumberInput fullWidth source="price" label={translate('events_sessions_prices.price')} validate={[required()]} />
                      </Grid> 
                      <PriceAudienceConfigurationFields formData={formData} />
                    </Grid>
                  )}
                </FormDataConsumer>
              </DialogContent>
              <DialogActions>
                <Button label="ra.action.cancel" onClick={handleCloseClick} disabled={loading} >
                  <IconCancel />
                </Button>
                <SaveButton
                  handleSubmitWithRedirect={
                    handleSubmitWithRedirect
                  }
                  pristine={pristine}
                  saving={saving}
                  disabled={loading}
                />
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  );
};

const MyActions = ({ basePath, data, resource }) => {

  const translate = useTranslate();   
    
  let linkBack = '';
  if (data && data.events_sessions_id)
      linkBack = '/events_sessions/' + data.events_sessions_id + '/prices';
        
  return (
    <>
      <TopToolbar>
        <Grid container spacing={2}>
          <Grid item xs={12}>   
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <Link
                to={linkBack}
                className=""
              >
                <ArrowBackIosNewIcon className="mr-2" />                        
              </Link>
              <Typography variant="h5" className="grey8">
                {translate('events_sessions_prices.edit_group')}
              </Typography>
            </div>  
                
          </Grid>
        </Grid> 
      </TopToolbar>
    </>
  );
};


export const EventsSessionsPricesEdit  = ({ id, ...props }) => {

  const translate = useTranslate();        
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
    
  const onSuccess = ({ data }) => {
    notify(translate('common.notification.updated'));        
    redirect('/events_sessions/' + data.events_sessions_id + '/prices');        
  };
        
  return (

    <Edit 
      title={<ElementTitle {...props}  />} 
      {...props} 
      id={id}
      mutationMode="pessimistic"
      actions={<MyActions {...props} />}
      onSuccess={onSuccess}   
    >
        
      <TabbedForm        
        toolbar={<FormToolbar />}
      >    
        <FormTab
          label={translate('classrooms.data')}
          contentClassName={classes.tab}
          path="" 
                
        > 
               
          <FormDataConsumer>
            {({ formData, ...rest }) => 
              <>                              
                <br/>
                <Grid container spacing={2}>
                  <PriceZoneInput eventSessionId={formData.events_sessions_id} />
                
                  <Grid item xs={6}>   
                  
                    <ReferenceInput                          
                        label={translate('events_sessions_prices.tickets_groups_id')}
                        source="tickets_groups_id" 
                        reference="tickets_groups" 
                        filter={{ associations_id: localStorage.getItem('associations_id') }} 
                        perPage={1000}
                        sort={{ field: 'priority', order: 'ASC' }}     
                        fullWidth
                        validate={[required()]}
                    >
                       <SelectInput optionText="name" optionValue="id" resettable />
                    </ReferenceInput>
    
                  </Grid> 
    
                  <PriceTotalCapacityInput eventSessionId={formData.events_sessions_id} />
                  
                  <Grid item xs={3}>   
                    <NumberInput fullWidth source="price" label={translate('events_sessions_prices.price')} validate={[required()]} />
                  </Grid> 
                  
                  <PriceAudienceConfigurationFields formData={formData} />
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.online_sales')}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <BooleanInput fullWidth source="actived" label={translate('events_sessions_prices.actived')} />
                  </Grid>
                  <Grid item xs={5}>
                    <DateTimeInput fullWidth source="sale_start" label={translate('events_sessions_prices.sale_start')} validate={[required()]} parse={dateParser} disabled={!formData.actived} />
                  </Grid>
                  <Grid item xs={5}>
                    <DateTimeInput fullWidth source="sale_finish" label={translate('events_sessions_prices.sale_finish')} validate={[required(), finishAfterStart('sale_start', 'actived')]} parse={dateParser} disabled={!formData.actived} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.box_office_sales')}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <BooleanInput fullWidth source="box_office_sale_enabled" label={translate('events_sessions_prices.box_office_sale_enabled')} />
                  </Grid>
                  <Grid item xs={5}>
                    <DateTimeInput fullWidth source="box_office_start" label={translate('events_sessions_prices.box_office_start')} validate={[required()]} parse={dateParser} disabled={!formData.box_office_sale_enabled} />
                  </Grid>
                  <Grid item xs={5}>
                    <DateTimeInput fullWidth source="box_office_finish" label={translate('events_sessions_prices.box_office_finish')} validate={[required(), finishAfterStart('box_office_start', 'box_office_sale_enabled')]} parse={dateParser} disabled={!formData.box_office_sale_enabled} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.reservations')}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <BooleanInput fullWidth source="box_office_reservation_enabled" label={translate('events_sessions_prices.box_office_reservation_enabled')} />
                  </Grid>
                  <Grid item xs={5}>
                    <DateTimeInput fullWidth source="reservation_period_start" label={translate('events_sessions_prices.reservation_period_start')} validate={[required()]} parse={dateParser} disabled={!formData.box_office_reservation_enabled} />
                  </Grid>
                  <Grid item xs={5}>
                    <DateTimeInput fullWidth source="reservation_period_finish" label={translate('events_sessions_prices.reservation_period_finish')} validate={[required(), finishAfterStart('reservation_period_start', 'box_office_reservation_enabled')]} parse={dateParser} disabled={!formData.box_office_reservation_enabled} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>{translate('events_sessions_prices.validators')}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <BooleanInput fullWidth source="require_check" label={translate('tickets_groups.require_check')} />
                  </Grid>
                  <Grid item xs={10}>
                    <TextInput fullWidth source="require_check_message" label={translate('tickets_groups.require_check_message')} disabled={!formData.require_check} />
                  </Grid>

                  <Grid item xs={3}>
                    <TextInput fullWidth source="priority" label={translate('events_sessions_prices.priority')} validate={[validateMaxLength(255,translate('common.validation.maxLength'))]} />
                  </Grid>

                </Grid>

              </>
            }
          </FormDataConsumer>

        </FormTab>
        
    
      </TabbedForm>
    </Edit>
  );
};

const ElementTitle = ({ record, ...props }) => {    
  const translate = useTranslate(); 
  return (<span>{translate('common.edit')} {record ? `"${record.name}"` : ''}</span>);
};

const FormToolbar = ({ ...props }) => {
    
  let redirect = '/events_sessions/' + props.record.events_sessions_id + '/prices';  
    
  return (
    <Toolbar {...props} className="user_form_toolbar" >
      <SaveButton />
      <DeleteButton redirect={redirect} mutationMode="pessimistic" />
    </Toolbar>
  );
};

const useStyles = makeStyles({
  comment: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tab: {
    maxWidth: '100%',
    display: 'block',
  },
});






export const EventsSessionsPricesCreate  = ({ id, ...props }) => {

  const translate = useTranslate();   
  const [info, setInfo] = useState();
    
  return (

    <Create {...props} >
      <SimpleForm 
        initialValues={{ events_sessions_id: props.events_sessions_id }}
        redirect="list"
      >                
        <Grid container spacing={2}>
          <Grid item xs={12}>                    
            <Typography variant="h5" className="grey">{translate('events_sessions_prices.create')}</Typography>
          </Grid>  
          <Grid item xs={11}>                    
            <TextInput fullWidth source="name" label={translate('common.name')} validate={[required(),validateMaxLength(255,translate('common.validation.maxLength'))]} />
          </Grid>          
        </Grid>   

      </SimpleForm> 
    </Create>
  );
};
